import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Search, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { useEmpresas, useEmpresa } from '@/features/empresa/hooks/useEmpresa';
import { buscarCep } from '@/features/empresa/services/empresaService';
import type { AtualizarEmpresaRequest } from '@/features/empresa/types';

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
              'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

const REGIME_TRIBUTARIO = [
  { value: 1, label: 'Simples Nacional' },
  { value: 2, label: 'Lucro Presumido' },
  { value: 3, label: 'Lucro Real' },
  { value: 4, label: 'MEI – Microempreendedor Individual' },
];

const PERFIL_SPED = [
  { value: 0, label: 'Perfil A' },
  { value: 1, label: 'Perfil B' },
  { value: 2, label: 'Perfil C' },
];

const ConfiguracaoEmpresa = () => {
  const { data: empresas = [], isLoading: isLoadingLista } = useEmpresas();
  const [empresaId, setEmpresaId] = useState<number | undefined>();
  const { empresa, isLoading, atualizar, isSalvando } = useEmpresa(empresaId);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const form = useForm<AtualizarEmpresaRequest>();

  // Seleciona automaticamente a primeira empresa
  useEffect(() => {
    if (empresas.length > 0 && !empresaId) {
      setEmpresaId(empresas[0].id);
    }
  }, [empresas, empresaId]);

  // Preenche o form quando carrega a empresa
  useEffect(() => {
    if (empresa) {
      form.reset({
        razaoSocial:          empresa.razaoSocial,
        nomeFantasia:         empresa.nomeFantasia ?? '',
        cnpjCpf:              empresa.cnpjCpf ?? '',
        cnae:                 empresa.cnae ?? '',
        inscricaoEstadual:    empresa.inscricaoEstadual ?? '',
        inscricaoMunicipal:   empresa.inscricaoMunicipal ?? '',
        inscricaoProdutor:    empresa.inscricaoProdutor ?? '',
        suframa:              empresa.suframa ?? '',
        naturezaJuridica:     empresa.naturezaJuridica ?? '',
        dtAbertura:           empresa.dtAbertura ? empresa.dtAbertura.slice(0, 10) : undefined,
        telefone:             empresa.telefone ?? '',
        email:                empresa.email ?? '',
        webSite:              empresa.webSite ?? '',
        cep:                  empresa.cep ?? '',
        logradouro:           empresa.logradouro ?? '',
        numero:               empresa.numero ?? '',
        complemento:          empresa.complemento ?? '',
        bairro:               empresa.bairro ?? '',
        cidade:               empresa.cidade ?? '',
        estado:               empresa.estado ?? '',
        codigoIbge:           empresa.codigoIbge,
        latitude:             empresa.latitude ?? 0,
        longitude:            empresa.longitude ?? 0,
        regimeTributario:     empresa.regimeTributario,
        incidenciaTributaria: empresa.incidenciaTributaria,
        metodoApropriacao:    empresa.metodoApropriacao,
        tipoContribuicao:     empresa.tipoContribuicao,
        criterioEscrituracao: empresa.criterioEscrituracao,
        impostoSobreLucro:    empresa.impostoSobreLucro,
        perfilSped:           empresa.perfilSped,
        regimeTributarioIssqn: empresa.regimeTributarioIssqn,
        indicadorRateioIssqn: empresa.indicadorRateioIssqn,
        taf:                  empresa.taf ?? '',
        rntrc:                empresa.rntrc ?? '',
        contadorId:           empresa.contadorId,
      });
    }
  }, [empresa, form]);

  const handleBuscarCep = async () => {
    const cep = form.getValues('cep') ?? '';
    setBuscandoCep(true);
    const res = await buscarCep(cep);
    setBuscandoCep(false);
    if (res) {
      form.setValue('logradouro', res.logradouro);
      form.setValue('complemento', res.complemento);
      form.setValue('bairro', res.bairro);
      form.setValue('cidade', res.localidade);
      form.setValue('estado', res.uf);
      form.setValue('codigoIbge', Number(res.ibge));
    }
  };

  const onSubmit = (data: AtualizarEmpresaRequest) => {
    atualizar(data);
  };

  if (isLoadingLista) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Configuração da Empresa</h1>
        <Button onClick={form.handleSubmit(onSubmit)} disabled={isSalvando || isLoading || !empresaId}>
          <Save className="h-4 w-4 mr-2" />
          {isSalvando ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      {/* Seletor de empresa (quando há múltiplas) */}
      {empresas.length > 1 && (
        <div className="max-w-sm space-y-2">
          <Label>Empresa / Filial</Label>
          <Select value={empresaId?.toString()} onValueChange={v => setEmpresaId(Number(v))}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => (
                <SelectItem key={e.id} value={e.id.toString()}>
                  {e.razaoSocial} {e.nomeFantasia ? `(${e.nomeFantasia})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!empresa && !isLoading && <p className="text-muted-foreground">Nenhuma empresa encontrada.</p>}

      {empresa && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Tabs defaultValue="identificacao">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="identificacao">Identificação</TabsTrigger>
                <TabsTrigger value="endereco">Endereço</TabsTrigger>
                <TabsTrigger value="tributacao">Tributação</TabsTrigger>
                <TabsTrigger value="outros">Outros</TabsTrigger>
              </TabsList>

              {/* ── Tab: Identificação ────────────────────────────────── */}
              <TabsContent value="identificacao" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="razaoSocial"
                    rules={{ required: 'Razão Social é obrigatória' }}
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Razão Social *</FormLabel>
                        <FormControl><Input maxLength={60} {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField control={form.control} name="nomeFantasia" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome Fantasia</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="cnpjCpf" render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNPJ / CPF</FormLabel>
                      <FormControl><Input maxLength={18} placeholder="00.000.000/0000-00" {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="cnae" render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNAE</FormLabel>
                      <FormControl><Input maxLength={7} placeholder="0000000" {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="naturezaJuridica" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Natureza Jurídica</FormLabel>
                      <FormControl><Input maxLength={80} {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                <Separator />
                <p className="text-sm font-medium text-muted-foreground">Inscrições</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField control={form.control} name="inscricaoEstadual" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inscrição Estadual</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="inscricaoMunicipal" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inscrição Municipal</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="inscricaoProdutor" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inscrição Produtor Rural</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="suframa" render={({ field }) => (
                    <FormItem>
                      <FormLabel>SUFRAMA</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                <Separator />
                <p className="text-sm font-medium text-muted-foreground">Contato</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField control={form.control} name="telefone" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl><Input maxLength={20} placeholder="(00) 00000-0000" {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail</FormLabel>
                      <FormControl><Input type="email" maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="webSite" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Site</FormLabel>
                      <FormControl><Input maxLength={80} placeholder="www.empresa.com.br" {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                <Separator />
                <p className="text-sm font-medium text-muted-foreground">Datas</p>

                <div className="grid grid-cols-2 gap-4 max-w-sm">
                  <FormField control={form.control} name="dtAbertura" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Abertura</FormLabel>
                      <FormControl><Input type="date" {...field} value={field.value ?? ''} /></FormControl>
                    </FormItem>
                  )} />
                </div>
              </TabsContent>

              {/* ── Tab: Endereço ─────────────────────────────────────── */}
              <TabsContent value="endereco" className="space-y-4 mt-4">
                <div className="flex gap-2 items-end max-w-xs">
                  <FormField control={form.control} name="cep" render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>CEP</FormLabel>
                      <FormControl><Input maxLength={10} placeholder="00000-000" {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <Button type="button" variant="outline" onClick={handleBuscarCep} disabled={buscandoCep}>
                    <Search className="h-4 w-4 mr-1" />
                    {buscandoCep ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FormField control={form.control} name="logradouro" render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Logradouro</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="numero" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número</FormLabel>
                      <FormControl><Input maxLength={10} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="complemento" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Complemento</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FormField control={form.control} name="bairro" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro</FormLabel>
                      <FormControl><Input maxLength={40} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="cidade" render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Cidade</FormLabel>
                      <FormControl><Input maxLength={60} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="estado" render={({ field }) => (
                    <FormItem>
                      <FormLabel>UF</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {UFS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField control={form.control} name="codigoIbge" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Código IBGE</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} value={field.value ?? ''}
                          onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="latitude" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latitude</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field}
                          onChange={e => field.onChange(Number(e.target.value))} />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="longitude" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Longitude</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field}
                          onChange={e => field.onChange(Number(e.target.value))} />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </TabsContent>

              {/* ── Tab: Tributação ───────────────────────────────────── */}
              <TabsContent value="tributacao" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="regimeTributario" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Regime Tributário *</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          {REGIME_TRIBUTARIO.map(r => (
                            <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="incidenciaTributaria" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Incidência Tributária</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          <SelectItem value="1">Cumulativo</SelectItem>
                          <SelectItem value="2">Não Cumulativo</SelectItem>
                          <SelectItem value="3">Misto</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="metodoApropriacao" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Método de Apropriação</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          <SelectItem value="1">Apropriação Direta</SelectItem>
                          <SelectItem value="2">Rateio Proporcional</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="perfilSped" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Perfil SPED</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          {PERFIL_SPED.map(p => (
                            <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="regimeTributarioIssqn" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Regime Tributário ISSQN</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          <SelectItem value="1">Microempresa Municipal</SelectItem>
                          <SelectItem value="2">Estimativa</SelectItem>
                          <SelectItem value="3">Sociedade de Profissionais</SelectItem>
                          <SelectItem value="4">Cooperativa</SelectItem>
                          <SelectItem value="5">Microempresário Individual (MEI)</SelectItem>
                          <SelectItem value="6">Microempresário e Empresa de Pequeno Porte (ME/EPP)</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="indicadorRateioIssqn" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Indicador Rateio ISSQN</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          <SelectItem value="0">Não</SelectItem>
                          <SelectItem value="1">Sim</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="impostoSobreLucro" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Imposto sobre o Lucro</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— Selecione —</SelectItem>
                          <SelectItem value="0">Não Incide</SelectItem>
                          <SelectItem value="1">Incide</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />
                </div>
              </TabsContent>

              {/* ── Tab: Outros ───────────────────────────────────────── */}
              <TabsContent value="outros" className="space-y-4 mt-4">
                <p className="text-sm font-medium text-muted-foreground">Transporte</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="taf" render={({ field }) => (
                    <FormItem>
                      <FormLabel>TAF – Termo de Autorização de Funcionamento</FormLabel>
                      <FormControl><Input maxLength={25} {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="rntrc" render={({ field }) => (
                    <FormItem>
                      <FormLabel>RNTRC – Registro Nacional de Transportadores</FormLabel>
                      <FormControl><Input maxLength={20} {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      )}
    </div>
  );
};

export default ConfiguracaoEmpresa;
